/**
 * Create-sheet API — the sheet-first creation flow (bao 2026-10-29 2x2
 * ruling): one FLUX-3 2x2@1024 sheet per generate click ($0.048, measured),
 * slice + white-key into 4 tiles, then per-pick SDS produce + mockup
 * polling. Identical inputs within 24h hit the worker's idempotency cache
 * ($0, born-ready job).
 *
 * Backend: mms-creator POST /api/customer/create/sheet, POST /cell,
 * GET /cell/:id, POST /buy, GET /buy/:id
 * (workers/mms-creator/src/routes/create-sheet.ts + create-buy.ts).
 */

import { CONFIG } from '../core/config.js'
import { store } from '../core/store.js'
import { fetchWithTimeout, handleAuthRequired, getSessionId } from './index.js'

function getAuthHeaders() {
  const token = store.getState('auth.token') || localStorage.getItem('mms_customer_token')
  // Guest identity (2026-11-02): the worker accepts X-Session-ID as
  // anon:<sid> for the free sheet quota — same convention as uploadImage.
  return token ? { Authorization: `Bearer ${token}` } : { 'X-Session-ID': getSessionId() }
}

async function postJson(path, body, timeoutMs) {
  const res = await fetchWithTimeout(
    `${CONFIG.FRONTEND_API}${path}`,
    {
      method: 'POST',
      headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    timeoutMs
  )
  if (!res.ok) {
    handleAuthRequired(res.status)
    const err = await res.json().catch(() => ({}))
    throw new Error(err?.error?.message || `HTTP ${res.status}`)
  }
  const json = await res.json()
  if (!json?.success) throw new Error(json?.error?.message || 'Request failed')
  return json.data
}

/**
 * Generate one 2x2 sheet and receive 4 keyed tiles.
 * Queued flow (2026-10-25, concurrency SSOT system-v1 §6.1): the API enqueues
 * the job and returns 202 {sheet_id}; we poll GET /sheet/:id until ready.
 * The returned shape is unchanged so callers (studio sheet flow) are unaware.
 * @param {{ prompt?: string, image_url?: string, style_ids?: string[] }} input
 * @returns {Promise<{ sheet_id: string, model_id: string, cost_usd: number|null,
 *   tiles: Array<{ slot: number, style_id: string|null, style_label: string|null,
 *                  image_url: string, transparent: boolean }> }>}
 */
export async function generateSheet({ prompt, image_url, style_ids, onStatus } = {}) {
  const submitted = await postJson(
    '/api/customer/create/sheet',
    {
      ...(prompt ? { prompt } : {}),
      ...(image_url ? { image_url } : {}),
      ...(style_ids?.length ? { style_ids } : {}),
    },
    30_000
  )
  if (submitted?.tiles) return submitted // inline fallback (no queue binding)
  return waitForSheet(submitted.sheet_id, { onStatus })
}

/** One sheet-status poll round. */
export async function pollSheet(sheetId) {
  const res = await fetchWithTimeout(
    `${CONFIG.FRONTEND_API}/api/customer/create/sheet/${encodeURIComponent(sheetId)}`,
    { headers: { ...getAuthHeaders() } },
    15_000
  )
  if (!res.ok) {
    handleAuthRequired(res.status)
    throw new Error(`HTTP ${res.status}`)
  }
  const json = await res.json()
  if (!json?.success) throw new Error(json?.error?.message || 'Poll failed')
  return json.data
}

/** Poll until ready / failed / timeout. FLUX-3 sheet renders run ~30-60s.
 * onStatus({ status, elapsedMs }) fires every poll round so the UI can show
 * honest progress instead of a frozen "0/9" (bao 2026-10-28). */
export async function waitForSheet(sheetId, { timeoutMs = 150_000, intervalMs = 2_000, onStatus } = {}) {
  const deadline = Date.now() + timeoutMs
  const started = Date.now()
  for (;;) {
    const state = await pollSheet(sheetId)
    if (state.status === 'ready') {
      return {
        sheet_id: sheetId,
        model_id: state.model_id ?? null,
        cost_usd: state.cost_usd ?? null,
        tiles: state.tiles ?? [],
      }
    }
    if (state.status === 'failed') throw new Error(state.error || 'Sheet generation failed')
    if (Date.now() > deadline) throw new Error('Generation timed out')
    try {
      onStatus?.({ status: state.status, elapsedMs: Date.now() - started })
    } catch {
      /* progress callback must never break the poll loop */
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
}

/**
 * P3 single-cell refine: re-render ONE style as a full 1024² canvas (no
 * slicing — the refined tile is sharper than a sheet cell). Same queued
 * contract as generateSheet: 202 {cell_id} → poll GET /cell/:id; identical
 * inputs within 24h hit the idempotency cache ($0, born-ready job).
 * @returns {Promise<{ cell_id: string, model_id: string, cost_usd: number|null,
 *   tile: { slot: number, tile_id: string, style_id: string|null,
 *           style_label: string|null, image_url: string, transparent: boolean } }>}
 */
export async function generateCell({ style_id, prompt, image_url, onStatus } = {}) {
  const submitted = await postJson(
    '/api/customer/create/cell',
    {
      style_id,
      ...(prompt ? { prompt } : {}),
      ...(image_url ? { image_url } : {}),
    },
    30_000
  )
  if (submitted?.tile) return submitted // inline fallback (no queue binding)
  return waitForCell(submitted.cell_id, { onStatus })
}

/** One cell-status poll round. */
export async function pollCell(cellId) {
  const res = await fetchWithTimeout(
    `${CONFIG.FRONTEND_API}/api/customer/create/cell/${encodeURIComponent(cellId)}`,
    { headers: { ...getAuthHeaders() } },
    15_000
  )
  if (!res.ok) {
    handleAuthRequired(res.status)
    throw new Error(`HTTP ${res.status}`)
  }
  const json = await res.json()
  if (!json?.success) throw new Error(json?.error?.message || 'Poll failed')
  return json.data
}

/** Poll until ready / failed / timeout (same render budget as a sheet). */
export async function waitForCell(cellId, { timeoutMs = 150_000, intervalMs = 2_000, onStatus } = {}) {
  const deadline = Date.now() + timeoutMs
  const started = Date.now()
  for (;;) {
    const state = await pollCell(cellId)
    if (state.status === 'ready') {
      return {
        cell_id: cellId,
        model_id: state.model_id ?? null,
        cost_usd: state.cost_usd ?? null,
        tile: state.tile,
      }
    }
    if (state.status === 'failed') throw new Error(state.error || 'Cell generation failed')
    if (Date.now() > deadline) throw new Error('Generation timed out')
    try {
      onStatus?.({ status: state.status, elapsedMs: Date.now() - started })
    } catch {
      /* progress callback must never break the poll loop */
    }
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
}

/**
 * One-click buy (2026-11-02): picked tile + product → server lane
 * (SDS produce → customer-owned design version → Shopify publish intent).
 * product_id is REQUIRED — it comes from the active blank carrier
 * (/api/blanks join), never a frontend default.
 * @returns {Promise<{ buy_id: string, status: string, eta_seconds?: number }>}
 */
export async function startBuy({ image_url, product_id } = {}) {
  return postJson('/api/customer/create/buy', { image_url, product_id }, 60_000)
}

/**
 * One buy-status poll round; the worker advances the state machine per call
 * (rendering → publishing → ready|failed).
 * @returns {Promise<{ buy_id: string, status: string,
 *   product_url?: string, handle?: string, error?: string }>}
 */
export async function pollBuy(buyId) {
  const res = await fetchWithTimeout(
    `${CONFIG.FRONTEND_API}/api/customer/create/buy/${encodeURIComponent(buyId)}`,
    { headers: { ...getAuthHeaders() } },
    15_000
  )
  if (!res.ok) {
    handleAuthRequired(res.status)
    throw new Error(`HTTP ${res.status}`)
  }
  const json = await res.json()
  if (!json?.success) throw new Error(json?.error?.message || 'Poll failed')
  return json.data
}

/**
 * Poll until ready / failed / timeout. SDS render (~2-5min) + Shopify publish
 * (inline-kicked, seconds). onUpdate fires every round for honest progress.
 * @param {(state: { status: string }) => void} [onUpdate]
 */
export async function waitForBuy(buyId, { timeoutMs = 10 * 60_000, intervalMs = 5_000, onUpdate } = {}) {
  const deadline = Date.now() + timeoutMs
  for (;;) {
    const state = await pollBuy(buyId)
    try {
      onUpdate?.(state)
    } catch {
      /* progress callback must never break the poll loop */
    }
    if (state.status === 'ready') return state
    if (state.status === 'failed') throw new Error(state.error || 'Product creation failed')
    if (Date.now() > deadline) throw new Error('Product creation timed out')
    await new Promise((resolve) => setTimeout(resolve, intervalMs))
  }
}
